import moment from "moment-timezone";
import "moment-duration-format";
import JSZip from "jszip";
import JSZipUtils from "jszip-utils";
import FileSaver from "file-saver";
import { cloneDeep, isEmpty, map, get, has, forEach } from "@utils/lodash";
import Blob from "blob";
import {
	JOB_INVOICED_TYPES,
	V3_INVOICE_TYPE_ID,
	OFFICER_VETTED_STATUS,
	INVOICE_STATUSES,
	JOB_ESTIMATE_APPROVAL_STATUS,
	SENIORITY_DRAFT_ENABLED_ORGANIZATIONS,
	NONRKPAY_INVOICE_STATUSES,
} from "@constants";
import { B2C_USER_CANCELLED, NONRKPAY_DISPUTE_REASON_STATUS } from "@constants/index";
import React from "react";
import { parse } from "date-fns";
import accountUtils from "./accountUtils";
import formUtils from "./formUtils";
import emailUtils from "./emailUtils";

const {
	navigator: { userAgent, vendor, maxTouchPoints },
} = window;
const isAppleDevice = vendor.toLowerCase().includes("apple");
const isSafari = isAppleDevice && /safari/gi.test(userAgent);
const isFireFox = /Firefox/gi.test(userAgent);
// First OR condition will identify all IE browsers except IE11
// Second OR condition is exclusive to IE11
const isIE =
	/MSIE \d|Trident.*rv:/.test(userAgent) ||
	(!!window.MSInputMethodContext && !!document.documentMode);
const isIPhone = isAppleDevice && /iphone/gi.test(userAgent);
const isIPad = isAppleDevice && !isIPhone && maxTouchPoints > 0;
const isIOSDevice = isIPad || isIPhone;
const isDesktopSafari = isSafari && !isIPhone && !isIPad;

export default {
	...accountUtils,
	...formUtils,
	...emailUtils,
	isAppleDevice,
	isSafari,
	isFireFox,
	isIE,
	isIPhone,
	isIPad,
	isIOSDevice,
	isDesktopSafari,

	/**
	 * Listens for HTML events on the DOM
	 * @param {String} eventIdentifier The type of event to listen for
	 * @param {Function} callback The method to execute when the event is fired
	 *
	 * @deprecated {@link Event} listeners can be added locally without needing a utility function
	 */
	registerEventListener(eventIdentifier, callback) {
		document.addEventListener(eventIdentifier, event =>
			callback(event.detail, eventIdentifier),
		);
	},

	/**
	 * Remove an event listener.
	 * @param {String} eventIdentifier The type of event to listen for
	 *
	 * @deprecated {@link Event} listeners can be added locally without needing a utility function
	 */
	removeEventListener(eventIdentifier) {
		document.removeEventListener(eventIdentifier);
	},

	/**
	 * Dispatches an HTML event on the DOM
	 * @param {String} eventIdentifier The type of event to listen for
	 * @param {Object} parameters An object containing event parameters
	 *
	 * @deprecated {@link Event Events} can be dispatched without needing a utility function
	 */
	dispatchEvent(eventIdentifier, parameters) {
		document.dispatchEvent(new CustomEvent(eventIdentifier, { detail: parameters }));
	},

	/**
	 * Pushes a toast notification HTML event to the DOM
	 * @param {Object} parameters An object containing the parameters to be dispatched along with the event
	 */
	notify(parameters) {
		this.dispatchEvent("notify", parameters);
	},

	dispatchUnauthorizedMessage() {
		this.dispatchEvent("unauthorized", {});
	},

	/**
	 * Formats a phone number to conform to the US standard (xxx) xxx-xxxx
	 * @param  {String} phoneNumber Phone number to format
	 * @return {String} Formatted phone number
	 */
	formatPhoneNumber(phoneNumber) {
		if (!phoneNumber) return phoneNumber;
		// normalize string and remove all unnecessary characters
		// Filter only numbers from the input
		const cleaned = phoneNumber.replace(/\D/g, "");

		// Check if the input is correct
		const match = cleaned.match(
			/^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/,
		);

		if (match) {
			return `${typeof match[1] !== "undefined" ? `+${match[1]} ` : ""}(${match[2]}) ${match[3]}-${match[4]}`;
		}

		return phoneNumber;
	},

	/**
	 * Removes all non-digit characters from a phone number string.
	 *
	 * @param {string} phoneNumber - The phone number string to be processed.
	 * @returns {string} - The phone number string with all non-digit characters removed.
	 */
	revertPhoneMask(phoneNumber) {
		return phoneNumber.replace(/\D/g, "");
	},

	/**
	 * Generic function to update a progress button
	 * to prevent repeated code, receive the following object structure
	 *
	 * @param params ```
	 * {
	 *     context  : [component context (this)],
	 *     property : [pb property state],
	 *     value    : [new pb value],
	 *     callback : [(optional) function executed after update]
	 * }
	 * ```
	 */
	updateProgressButton({ context, property, value, callback }) {
		const state = cloneDeep(context.state);

		// Set value
		state[property] = value;

		context.setState(state, () => {
			// Prevent clean up if it's loading
			if (value === "loading") {
				if (callback) callback();

				return;
			}

			// Clean progressButton after a 800ms to let component finish the animation
			setTimeout(() => {
				state[property] = "";

				context.setState(state, () => {
					if (callback) callback(state);
				});
			}, 800);
		});
	},

	/**
	 * @deprecated Handles API errors
	 *
	 * @param {String} defaultMessage Error message to be displayed
	 * @param {Object} error Error object received from the API
	 * @param {String} loadingProperty Property to be updated; Can be dot notation ('form.button', 'state.loading', etc.)
	 * @param {Object} context Component Context
	 * @param {Function} callback Callback function to be executed after update the state
	 * @param {String} defaultTitle Alert title. Defaults to "Oops".
	 * @param {Boolean} hideAlert Show or hide the alert. Defaults to `false`.
	 */
	genericAPIErrorHandler(
		defaultMessage,
		error,
		loadingProperty,
		context,
		callback,
		defaultTitle = "Oops",
		hideAlert = false,
	) {
		const { response } = error;
		const isRequestFailed = response === undefined;
		let title = defaultTitle;
		let message = isRequestFailed
			? "Request Failed, please check your Internet connection"
			: defaultMessage;

		if (!isRequestFailed) {
			/**
			 * For catched failed promises, the response
			 * body is inside the `data` object.
			 * In the other hand, for error callback responses
			 * the response is inside the `body` object.
			 */
			const responseBodyKey = error.response.data === undefined ? "body" : "data";

			/**
			 * Use error statusText from
			 * the response as title
			 * otherwise use defaultTitle
			 * instead
			 */
			title = get(error, ["response", "statusText"], title);

			message = get(error, `response.${responseBodyKey}.ErrorMessage`);
			message = !message ? get(error, `response.${responseBodyKey}.Message`) : message;
			message = !message ? get(error, `response.${responseBodyKey}.message`) : message;
			message = !message ? get(error, "message") : message;
			message = !message ? get(error, "Message") : message;
		}

		if (!hideAlert) {
			this.notify({
				type: "error",
				title,
				message,
			});
		}

		if (loadingProperty && context) {
			const state = cloneDeep(context.state);

			// Update property
			state[loadingProperty] = false;

			context.setState(state, () => {
				if (callback) callback(state);
			});
		}
	},

	/**
	 * @deprecated Handles input field changes based on the given parameters.
	 *
	 * The component that uses this generic function must have defined:
	 * - Form data as an object on States called "form"
	 * - Fields validation as an object on States called "validations"
	 * - onChange callbacks needs be called "postOnChangeCallbacks" and should
	 *   be defined globally in the component itself
	 * - The parameter "firstLevelProperty" is optional and is only used for
	 *   setting the value in "form" structure, is not being used on "postOnChangeCallbacks"
	 *
	 * @param {String} property Property field name in form structure, can be in dot notation
	 * @param {String} type Field type
	 * @param {Object} context Component Context
	 * @param {Event} event The HTML event to handle
	 */
	genericOnFieldChangeHandler(property, type, context, event) {
		const state = cloneDeep(context.state);
		let value;
		let validValue = false;

		switch (type) {
			case "field":
				value = event.target.value;
				break;

			case "select":
				value = event ? event.value : null;
				break;

			case "checkbox":
				value = event.target.checked;
				break;

			case "date":
			case "value":
				value = event;
				break;

			default:
				value = "";
				break;
		}

		// Get last value from given property
		const lastValue = state.form[property] || value;

		// Update property
		state.form[property] = value;

		/**
		 * Clear out validation for current property, if there is one
		 * by checking first level property name also, just in case is validating a group of items
		 * or one by one using deeper property name with dot annotation string
		 */
		if (state.validations && has(state.validations, property)) {
			state.validations[property] = null;
		}

		// Check if it's a valid value to be used on the property postOnChangeCallback
		validValue = typeof value === "boolean";
		validValue = !validValue ? typeof value === "number" : validValue;
		validValue = !validValue ? typeof value === "string" && !isEmpty(value) : validValue;

		if (type === "select" && value === null) validValue = true;
		// First update the state, and then run the callback function
		context.setState(state, () => {
			/**
			 * Execute a proper postOnChangeCallback if it's defined for the current property, and
			 * the value is not empty
			 */
			if (
				context.postOnChangeCallbacks &&
				has(context.postOnChangeCallbacks, property) &&
				validValue
			) {
				context.postOnChangeCallbacks[property](value, lastValue);
			}
		});
	},

	/**
	 * @param {String} value The value to remove non-numerical characters from
	 * @return {String} The text provided in `value` with all non-numerical characters removed
	 *
	 * @deprecated The `string` replacement functionality this method offers should be done locally without needing a utility function
	 */
	getDigits(value) {
		if (!value) return value;

		return value.replace(/\D/g, "");
	},

	/**
	 * @param {String} time HH:MM AA representation of a time
	 * @returns {Number} how many minutes have passed since 12:00 AM
	 */
	convertTimeToMinutes(time) {
		const timeSplit = time.split(":");
		const values = timeSplit.map(value => parseInt(value, 10));
		let hours = values[0];
		const minutes = values[1];
		if (isDesktopSafari || isIE) {
			const [, m] = timeSplit;
			const isPM = m.toLowerCase().includes("pm");
			if (hours === 12 && !isPM) {
				hours = 0;
			} else if (isPM && hours < 12 && hours > 0) {
				hours += 12;
			}
		}
		const timeInMinutes = hours * 60 + minutes;
		return timeInMinutes;
	},

	/**
	 * Finds an officer's vetted status text
	 * @param {Number} status A value identifying an officer's vetted status
	 * @return {String} Text to inform a user what an officer's vetted status is
	 *
	 * @deprecated This logic should be performed locally without needing a utility function
	 */
	getVettedStatusText(status) {
		const { lang } = global;
		const vettedStatus = OFFICER_VETTED_STATUS.find(_status => _status.value === status);

		return vettedStatus ? vettedStatus.label : lang.t("Unspecified");
	},

	/**
	 * Formats a decimal number as US currency
	 * @param {Number} value The value to format
	 * @param {Boolean} isDecimalRequired Whether or not a decimal is required for whole numbers. Defaults to `true`.
	 * @return {String} A number fixed to 2 decimal places with a $ symbol before it
	 */
	currencyFormatter(value, isDecimalRequired = true) {
		if (Number.isNaN(value) || value == null) {
			return isDecimalRequired ? "$0.00" : "$0";
		}

		const parsedValue = typeof value === "string" ? parseFloat(value) : value;
		const amount = parsedValue.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, "$1,");

		return `$${isDecimalRequired ? amount : amount.substr(0, amount.indexOf("."))}`;
	},

	/**
	 * Formats a int to decimal number
	 * @param {Number} value The value to format
	 * @return {Number} The value formatted to 2 decimal places
	 */
	decimalFormatter(value) {
		if (Number.isNaN(value) || value == null) {
			return 0;
		}

		return value / 100;
	},

	/**
	 * Constructs a formatted `string` given a city and a state (city, state)
	 * @param {String} city
	 * @param {String} state
	 *
	 * @deprecated This logic should be performed locally without needing a utility function
	 */
	constructShortAddress(city, state) {
		let shortAddress = "";

		if (city && state) shortAddress = `${city}, ${state}`;
		else if (city && !state) shortAddress = city;
		else if (!city && state) shortAddress = state;

		return shortAddress;
	},

	/**
	 * Finds the current user's default payment method if one has been set up
	 * @param {Array} paymentMethods An array of payment methods available to the user
	 * @returns A formatted object containing payment method information if a default method is found. Otherwise, this returns `null`
	 *
	 * @deprecated This method is only used in one file, so the logic could be done locally without the need for a utility function
	 */
	getDefaultPaymentMethod(paymentMethods) {
		const defaultPaymentMethod = paymentMethods.find(
			item => item.type === "card" && item.isDefault,
		);

		if (defaultPaymentMethod) {
			return {
				id: defaultPaymentMethod.id,
				type: "card",
				cardNumber: defaultPaymentMethod.cardNumber,
				cardType: defaultPaymentMethod.cardType.replace(" ", "-").toLowerCase(),
			};
		}

		return null;
	},

	/**
	 * Consolidates all available payment methods into a single array
	 * @param {Object} obj
	 * @param {Array} obj.BankAccounts
	 * @param {Array} obj.PaymentCards
	 * @returns {Array} An array with formatted bank account and credit card objects combined
	 */
	extractPaymentMethods({ BankAccounts, PaymentCards }) {
		const bankAccounts = (BankAccounts || []).map(account => ({
			id: account.BankAccountId,
			type: "bank_account",
			bankName: account.BankName,
			accNumber: account.AccountNumber.substr(account.AccountNumber.lastIndexOf("*") + 1),
			accStatus: account.BankAccountStatus,
		}));

		const cards = (PaymentCards || []).map(card => ({
			id: card.PaymentCardId,
			type: "card",
			isDefault: card.IsDefaultCard,
			cardNumber: card.CardNumber.replace(/[.]/g, "*"),
			cardType: card.CardType.replace(" ", "-").toLowerCase(),
			expiry: `${card.ExpirationMonth.toString().length === 1
					? `0${card.ExpirationMonth}`
					: card.ExpirationMonth
				}/${card.ExpirationYear.toString().substr(2)}`,
		}));

		return [...bankAccounts, ...cards];
	},

	/**
	 * Creates select options based on the response from an API call.
	 *
	 * @deprecated This method is only currently used in a thunk related to payments.
	 * After merging FE-812, this method will no longer be required and can be deleted at that time.
	 */
	buildSelectArrayOptionsFromAPIResponse({
		labelProperty,
		valueProperty,
		imageURL,
		subLabel,
		data,
	}) {
		const returnData = [];
		if (data.length) {
			forEach(data, item => {
				if (imageURL) {
					returnData.push({
						value: item[valueProperty],
						label: item[labelProperty],
						imageURL: item[imageURL],
						subLabel: item[subLabel],
					});
				} else {
					returnData.push({
						value: item[valueProperty],
						label: item[labelProperty],
					});
				}
			});
		}
		return returnData;
	},

	/** @deprecated unused method */
	getInvoiceStatusFromStatusId(statusId, status) {
		const result = {
			label: status,
		};

		switch (statusId) {
			case INVOICE_STATUSES.PENDING_REVIEW:
			case INVOICE_STATUSES.AMENDED:
			case INVOICE_STATUSES.PENDING_PAYMENT:
			case INVOICE_STATUSES.PENDING_PROCESSING:
			case INVOICE_STATUSES.PENDING_CLIENT_REVIEW:
				result.type = "warning";
				break;
			case INVOICE_STATUSES.PAYMENT_REJECTED:
			case INVOICE_STATUSES.DISPUTED:
			case INVOICE_STATUSES.VOID:
				result.type = "danger";
				break;
			default:
				result.type = "success";
				break;
		}

		return result;
	},

	/**
	 * @param {Number} statusId The Id number for an invoice status
	 * @param {String} status The description of the invoice status
	 * @param {String} userName The user who last updated the invoice status
	 * @returns {String} Text to indicate an invoice's status with all related information
	 *
	 * @deprecated This method is currently only used in `<PaymentDetails>`, so the logic can be moved to that file and this method can be deleted
	 */
	createStatusHistoryText(statusId, status, userName) {
		const { lang } = global;
		let invoiceText = "";

		switch (statusId) {
			case INVOICE_STATUSES.CREATED:
			case INVOICE_STATUSES.AMENDED:
			case INVOICE_STATUSES.PAID:
			case INVOICE_STATUSES.DEPOSITED:
				invoiceText = userName
					? `${userName} ${status} ${lang.t("Invoice")}`
					: `${lang.t("Invoice was")} ${status}`;
				break;
			case INVOICE_STATUSES.DISPUTED:
				invoiceText = userName
					? `${userName} ${lang.t("filed a dispute for invoice")}`
					: lang.t("Invoice was disputed");
				break;
			default:
				invoiceText = userName
					? `${userName} ${lang.t("changed invoice status to")} ${status}`
					: `${lang.t("Invoice status was changed to")} ${status}`;
				break;
		}

		return invoiceText;
	},

	/**
	 * @param {Array} filesToDownload Array of objects of the format `{ filename : String, url : String }`
	 * @param {String} downloadFileName Name of the downloaded file on the user's system. Defaults to 'download.zip'.
	 */
	downloadFiles(filesToDownload, downloadFileName = "download.zip") {
		return new Promise((resolve, reject) => {
			const zip = new JSZip();

			const fetchFiles = () =>
				new Promise((_resolve, _reject) => {
					const promises = map(
						filesToDownload,
						({ fileName, url }) =>
							new Promise((resolveRead, rejectRead) => {
								JSZipUtils.getBinaryContent(url, (error, data) => {
									if (error) return rejectRead(error);

									zip.file(fileName, data, url);

									resolveRead();

									return null;
								});
							}),
					);

					Promise.all(promises).then(_resolve).catch(_reject);
				});

			const generateZip = () =>
				new Promise((_resolve, _reject) => {
					zip.generateAsync({ type: "blob" }).then(_resolve).catch(_reject);
				});

			const downloadFile = content => {
				FileSaver.saveAs(content, downloadFileName);

				return Promise.resolve();
			};

			fetchFiles().then(generateZip).then(downloadFile).then(resolve).catch(reject);
		});
	},

	/**
	 * Saves a blob as a file on the user's system
	 * @param {Blob} data A `Blob` object to save to the user's system; Typically returned from an API response.
	 * @param {String} fileName Name of the downloaded file on the user's system
	 */
	downloadFromBlobResponse(data, fileName) {
		// used for IE
		if (window.navigator.msSaveOrOpenBlob) window.navigator.msSaveOrOpenBlob(data, fileName);
		else {
			const a = document.createElement("a");
			const url = window.URL.createObjectURL(data);

			a.style.display = "none";
			a.href = url;
			a.setAttribute("download", fileName);

			document.body.appendChild(a);

			a.click();

			window.URL.revokeObjectURL(url);
		}
	},

	/**
	 * Saves a blob as a file on the user's system
	 * @param {Blob} data A `Blob` object to save to the user's system; Typically returned from an API response.
	 * @param {String} fileName Name of the downloaded file on the user's system
	 */
	downloadReportFromBlobResponse(data, fileName) {
		return this.downloadFromBlobResponse(data, fileName);
	},

	/**
	 * @param {Object} obj
	 * @param {String} obj.Status
	 * @returns {{ value : String, label : String}} An object contained the appropriate label type given a job's payment status
	 */
	getNewJobStatus({ Status }) {
		const result = { value: Status };
		if (
			Status === "PendingReview" ||
			Status === "Amended" ||
			Status === "PendingPayment" ||
			Status === "PaymentProcessing"
		)
			result.label = "warning";
		else if (Status === "PaymentRejected" || Status === "Disputed" || Status === "Cancelled")
			result.label = "danger";
		else result.label = "success";
		return result;
	},

	/**
	 * Determines a report's filename from the response headers of an API request
	 * @param {String} contentDisposition The value of the `content-disposition` response header
	 * @returns {String} URI-decoded filename for the exported report
	 *
	 * @deprecated This method is only used in `Report.js`, so this method can be removed after logic has been migrated to that file
	 */
	getFileNameByContentDisposition(contentDisposition) {
		const regex = /filename[^;=\n]*=(UTF-8(['"]*))?(.*)/;
		const matches = regex.exec(contentDisposition);
		let filename;

		if (matches != null && matches[3]) filename = matches[3].replace(/['"]/g, "");

		return decodeURI(filename);
	},

	/**
	 * Returns the actions that can be performed on the job object by the given user
	 * @param {Object} user The user viewing the job
	 * @param {Object} jobEventObject The job event details
	 */
	getJobActionPermissions(user, jobEventObject) {
		let allowEdit = false;
		let allowPost = false;
		let allowRemovePost = false;
		let allowDelete = false;
		let allowRestore = false;
		let allowDuplicate = true;
		let allowAssign = false;
		let allowChange = false;
		let allowUnassign = false;
		let allowAddNotes = false;
		let allowDeleteAbandonedJob = false;

		const organizationId = user?.Organization?.Id;
		const estimateApprovalData = jobEventObject.JobEventInvoicEstimateApproval;

		if (
			!(user.isClient() || user.isLEA()) ||
			(user.isLEA() && jobEventObject.AgencyId === organizationId)
		) {
			if (!jobEventObject.Deleted) {
				// An "abandoned" job can be deleted only when it has no assigned officers
				// or when the assigned officers didn't clock-in
				allowDeleteAbandonedJob =
					jobEventObject.ApplicantsAssigned === 0 ||
					!(jobEventObject.Jobs || [])
						.filter(item => item.AssignedOfficers && item.AssignedOfficers.length > 0)
						.reduce((acc, item) => [...acc, ...item.AssignedOfficers], [])
						.some(item => item.ClockIn !== null);

				allowEdit = true;

				if (!jobEventObject.IsClosed) {
					allowPost = !jobEventObject.Posted;
					allowRemovePost = jobEventObject.Posted;
					allowDelete = true;
				}

				if (user.isSuperAdmin()) allowEdit = false;
			}
			// posting permissions according to Estimate Approval

			const isEstimateDenied =
				jobEventObject?.JobEventInvoicEstimateApproval?.Status ===
				JOB_ESTIMATE_APPROVAL_STATUS.DENIED.value;

			// TODO: How to validate jobs independently?
			const isJobFilled =
				jobEventObject.ApplicantsNeeded === jobEventObject.ApplicantsAssigned;

			// Allow assign if the job is not filled and the estimate is not denied.
			allowAssign = !isJobFilled && !isEstimateDenied;
			allowChange = true;

			if (estimateApprovalData && jobEventObject.RequireClientInvoiceEstimateApproval) {
				const { IsApproved: isEstimateApproved } = estimateApprovalData;
				allowAssign = !isJobFilled && !isEstimateDenied && (isEstimateApproved ?? false);
				allowPost = !jobEventObject.IsClosed && (isEstimateApproved ?? false);
			}
		}

		allowRestore = jobEventObject.Deleted;
		allowDuplicate = !user.isSuperAdmin() && jobEventObject.AgencyId === organizationId;
		allowAddNotes = !user.isClient();
		allowUnassign = user.isLEA() || user.isCoordinator() || user.isAgencyAdmin();

		if (user.isLEA()) {
			const { AgencyId } = jobEventObject;
			const isSameOrganization = AgencyId === organizationId;
			// override permissions for LEAs if the job belongs to another organization
			allowAddNotes = isSameOrganization;
			allowUnassign = isSameOrganization;
		}

		if (user.IsViewOnly) {
			allowEdit = false;
			allowPost = false;
			allowRemovePost = false;
			allowDelete = false;
			allowRestore = false;
			allowDuplicate = false;
			allowAssign = false;
			allowChange = false;
			allowUnassign = false;
			allowAddNotes = false;
			allowDeleteAbandonedJob = false;
		}

		return {
			allowEdit,
			allowPost,
			allowRemovePost,
			allowDelete,
			allowRestore,
			allowDuplicate,
			allowAssign,
			allowChange,
			allowUnassign,
			allowAddNotes,
			allowDeleteAbandonedJob,
			allowViewHistory: true,
		};
	},

	/**
	 * Function to concatenate Address, City,
	 * State and Zip values
	 * @param {String} param.address
	 * @param {String} param.city
	 * @param {String} param.state
	 * @param {String} param.zip
	 * @param {Bool} multilined
	 * @returns {String}
	 *
	 * @deprecated Any instance of this method should be removed and replaced with either local logic or semantic HTML text rendering
	 */
	concatenateAddressDetails(
		{ address, city, state, zip, latitude, longitude },
		{ useMultiline = false } = {},
	) {
		let top = "";
		let bottom = "";

		// Add Address to result
		if (address) top += address;

		// Add City to result
		if (city && !isEmpty(top) && !useMultiline) bottom += `, ${city}`;
		else if (city && !isEmpty(top)) bottom += city;

		// Add State to result
		if (state && !isEmpty(bottom)) bottom += `, ${state}`;
		else if (state && isEmpty(bottom)) bottom += state;

		// Add Zip to result
		if (zip && state) bottom += ` ${zip}`;
		else if (zip && !state && !isEmpty(bottom) && !useMultiline) bottom += `, ${zip}`;
		else if (zip && isEmpty(bottom)) bottom += zip;

		if (isEmpty(top + bottom) && useMultiline) {
			return {
				top: latitude === null ? "" : `Lat: ${latitude}`,
				bottom: longitude === null ? "" : `Lng: ${longitude}`,
			};
		}

		// Return object if multiline address is needed
		if (useMultiline) return { top, bottom };

		if (!isEmpty(top + bottom)) return top + bottom;

		return `${latitude || ""}${latitude && longitude ? ", " : ""}${longitude || ""}`;
	},

	/**
	 * @param {String|Number} n The number to abbreviate
	 * @returns {String} an abbreviated version of the provided number (ex. 10,000 => 10k)
	 *
	 * @todo Review this method and see that abbreviations as well as the method for determining them are correct
	 */
	abbreviateNumber(n) {
		const number = +parseFloat(n).toFixed(2);
		const SI_SYMBOL = ["", "k", "M", "G", "T", "P", "E"];

		// What tier? (determines SI symbol)
		const tier = (Math.log10(number) / 3) | 0; // eslint-disable-line no-bitwise

		// If zero, we don't need a suffix
		if (tier === 0) return number;

		// Get suffix and determine scale
		const suffix = SI_SYMBOL[tier];
		const scale = 10 ** (tier * 3);

		// Scale the number
		const scaled = number / scale;

		// Format number and add suffix
		return scaled.toFixed(1) + suffix;
	},

	/**
	 * Converts a base-64 encoded image to a Blob
	 * @param {String} b64Data A base-64 encoding of an image
	 * @param {String} contentType
	 *
	 * @deprecated This method is only used in `Report.js`, so it can be deleted after the logic has been migrated to that file
	 */
	base64ToBlob(base64String, contentType = "") {
		const byteCharacters = atob(base64String);
		const byteArrays = [];
		const sliceSize = 512;

		for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
			const slice = byteCharacters.slice(offset, offset + sliceSize);
			const byteNumbers = new Array(slice.length);

			for (let i = 0; i < slice.length; i++) byteNumbers[i] = slice.charCodeAt(i);

			const byteArray = new Uint8Array(byteNumbers);
			byteArrays.push(byteArray);
		}

		return new Blob(byteArrays, { type: contentType });
	},

	/**
	 * Converts a timestamp to a human-readable "from now" format
	 * @param {Number} date time in milliseconds
	 *
	 * @deprecated This method is only used in `NotificationsModal.js`, so it can be deleted once logic has been migrated to that file
	 * @todo Update this method to use language strings instead of hardcoded English text
	 */
	timeSince(date) {
		const minute = 60;
		const hour = minute * 60;
		const day = hour * 24;
		const month = day * 30;
		const year = day * 365;

		const elapsed = Math.floor((Date.now() - date) / 1000);

		if (elapsed < minute) {
			return "just now";
		}

		// get an array in the form of [number, string]
		const result = (elapsed < hour && [Math.floor(elapsed / minute), "minute"]) ||
			(elapsed < day && [Math.floor(elapsed / hour), "hour"]) ||
			(elapsed < month && [Math.floor(elapsed / day), "day"]) ||
			(elapsed < year && [Math.floor(elapsed / month), "month"]) || [
				Math.floor(elapsed / year),
				"year",
			];

		// pluralize and append suffix
		return `${result[0]} ${result[1]}${result[0] === 1 ? "" : "s"} ago`;
	},

	/**
	 * Converts a date object or string to a date string using a locale-based format
	 * @param {String|Date} rawDate A date object or string to format. Defaults to `new Date()`.
	 * @param {String} locale The locale to use when formatting. Defaults to 'sv-SE'.
	 * @param {String} timeZoneId A timezone to convert the date to. Works best when the `rawDate` parameter represents a UTC timestamp.
	 * @param {'short'|'long'|'w-long'|'w-long-no-year'|'numeric'|'dt-short'|'dt-num'|'dt-num-tz'|'2-digit'} format A preset list of options to format the date string.
	 * Default format is '2-digit'.
	 * @returns {String} A date string formatted with the locale and options specified using {@link Date.toLocaleString}
	 */
	// eslint-disable-next-line default-param-last
	formatDate(rawDate = new Date(), locale = "sv-SE", timeZoneId, format) {
		const options = {
			timeZone: timeZoneId,
		};
		switch (format) {
			case "short":
				options.year = "numeric";
				options.month = "short";
				options.day = "numeric";
				break;
			case "long":
				options.year = "numeric";
				options.month = "long";
				options.day = "numeric";
				break;
			case "w-long":
				options.year = "numeric";
				options.month = "long";
				options.day = "numeric";
				options.weekday = "long";
				break;
			case "w-long-no-year":
				options.month = "long";
				options.day = "numeric";
				options.weekday = "long";
				break;
			case "numeric":
				options.year = "numeric";
				options.month = "numeric";
				options.day = "numeric";
				break;
			case "dt-short":
				options.year = "numeric";
				options.month = "short";
				options.day = "numeric";
				options.hour = "numeric";
				options.minute = "2-digit";
				options.hour12 = true;
				break;
			case "dt-num":
				options.year = "numeric";
				options.month = "numeric";
				options.day = "numeric";
				options.hour = "numeric";
				options.minute = "2-digit";
				options.hour12 = true;
				break;
			case "dt-num-tz":
				options.year = "numeric";
				options.month = "numeric";
				options.day = "numeric";
				options.hour = "numeric";
				options.minute = "2-digit";
				options.hour12 = true;
				options.timeZoneName = "short";
				break;
			case "dt-short-mil":
				options.year = "numeric";
				options.month = "short";
				options.day = "numeric";
				options.hour = "numeric";
				options.minute = "2-digit";
				options.hour12 = false;
				break;
			case "dt-num-mil":
				options.year = "numeric";
				options.month = "numeric";
				options.day = "numeric";
				options.hour = "numeric";
				options.minute = "2-digit";
				options.hour12 = false;
				break;
			case "dt-num-tz-mil":
				options.year = "numeric";
				options.month = "numeric";
				options.day = "numeric";
				options.hour = "numeric";
				options.minute = "2-digit";
				options.hour12 = false;
				options.timeZoneName = "short";
				break;
			case "2-digit":
			default:
				options.year = "numeric";
				options.month = "2-digit";
				options.day = "2-digit";
				break;
		}

		if (!rawDate) return "";

		let date = rawDate instanceof Date ? rawDate : parse(rawDate, "yyyy-M-d", new Date());
		if (Number.isNaN(date.getTime())) date = new Date(rawDate);
		if (isIOSDevice && format !== "noslash") {
			return this.fixIELocaleString(
				date.toLocaleString(locale, options).replace(/\//gi, "-"),
			);
		}
		return this.fixIELocaleString(date.toLocaleString(locale, options));
	},

	/**
	 * IE sometimes adds empty string characters (UTF-16 8206), making dates and times invalid.
	 * This method finds and eliminates these characters and returns a valid string.
	 * @param {String} string string to be cleaned
	 * @returns {String} A version of the string that can be processed normally on Internet Explorer
	 */
	fixIELocaleString: string =>
		string
			.split("")
			.filter(c => c.charCodeAt() !== 8206)
			.join(""),

	/**
	 * Converts a date object or string to a time string in the HH:MM format
	 * @param {String|Date} rawDate A date object or string to format. Defaults to `new Date()`.
	 * @param {Boolean} hour12 Whether the formatted time should be returned in 12-hour or 24-hour (military) format. Defaults to `false`.
	 * @param {String} timeZoneId A timezone to convert the date to. Works best when the `rawDate` parameter represents a UTC timestamp.
	 * @returns {String} A time string formatted with the options specified using {@link Date.toLocaleString}
	 */
	// eslint-disable-next-line default-param-last
	formatTime(rawDate = new Date(), hour12 = false, timeZoneId, addSeconds = false) {
		const date = rawDate instanceof Date ? rawDate : new Date(rawDate);
		if (Number.isNaN(date.getTime())) {
			return "";
		}
		/* hourCycle 'h23' prevents midnight from ever appearing as 24:00, as it does in some places
		 * outside of the US (and sometimes in the US too...)
		 * However, both hour12 and hourCycle cannot be used together. Therefore, we only use hour12 if
		 * it's explicity set to true.
		 */
		return this.fixIELocaleString(
			date.toLocaleString("en-US", {
				hour: hour12 ? "numeric" : "2-digit",
				minute: "2-digit",
				second: addSeconds ? "2-digit" : undefined,
				hour12: this.isIE ? hour12 : hour12 || undefined,
				hourCycle: hour12 ? undefined : "h23",
				timeZone: timeZoneId,
			}),
		);
	},
	/**
	 * Converts a date object or string to a time string in the HH:MM format
	 * @param {String|Date} rawDate A date object or string to format. Defaults to `new Date()`.
	 * @param {Boolean} hour12 Whether the formatted time should be returned in 12-hour or 24-hour (military) format. Defaults to `false`.
	 * @param {String} timeZoneId A timezone to convert the date to. Works best when the `rawDate` parameter represents a UTC timestamp.
	 */
	// eslint-disable-next-line default-param-last
	formatTimeWithZero(time, hour12 = false, timeZoneId) {
		let formattedTime = this.formatTime(new Date(`${time}Z`), hour12, timeZoneId);
		const hours = formattedTime.split(":")[0];
		if (hours.length === 1) {
			formattedTime = `0${formattedTime}`;
		}
		return formattedTime;
	},

	/**
	 * @param {Date} date a date you gonna make the operation
	 * @param {string} time tha hour of the job/event based on your timezone use formatTime() to get the proper value
	 * @param {number} timeToOperateInMinutes time un minutes to add or subtract
	 * @param {boolean} add defines if you want to add or subtract the minutes
	 * @returns {string} a time string hour24 based
	 */

	addSubtractTime(date, time, timeToOperateInMinutes, add = true) {
		const [_hours, _minutes] = this.convertHour12to24(time).split(":");
		const isNeededLeftZero = number => (String(number).length < 2 ? "0" : "");

		if (_hours) {
			const _hour = `T${isNeededLeftZero(_hours)}${_hours}:${isNeededLeftZero(_minutes)}${_minutes}:00`;
			const _date = new Date(`${date?.split("T")[0]}${_hour}`);

			const numberOfMlSeconds = _date.getTime();
			const addSubMlSeconds = timeToOperateInMinutes * 60 * 1000;
			let newDateObj = new Date(numberOfMlSeconds - addSubMlSeconds);

			if (add) {
				newDateObj = new Date(numberOfMlSeconds + addSubMlSeconds);
			}

			const calculatedHours = newDateObj.getHours();
			const calculatedMinutes = newDateObj.getMinutes();

			return `${isNeededLeftZero(calculatedHours)}${calculatedHours}:${isNeededLeftZero(calculatedMinutes)}${calculatedMinutes}`;
		}

		return new Date();
	},

	addSubtractDate(date, daysToOperate, add = true) {
		if (date) {
			const _date = new Date(date);
			let newDateObj = _date.setDate(_date.getDate() - daysToOperate);

			if (add) {
				newDateObj = _date.setDate(_date.getDate() + daysToOperate);
			}

			return new Date(newDateObj).toISOString();
		}

		return new Date().toISOString();
	},

	convertHour24ToHour12(time24h) {
		let _time = time24h;
		// Check correct time format and split into components
		_time = _time.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [_time];

		if (_time.length > 1) {
			// If time format correct
			_time = _time.slice(1); // Remove full string match value
			_time[5] = +_time[0] < 12 ? " AM" : " PM"; // Set AM/PM
			_time[0] = +_time[0] % 12 || 12; // Adjust hours
		}
		return _time.join(""); // return adjusted time or original string
	},

	convertHour12to24(time12h) {
		const [time, modifier] = time12h.split(" ");

		// eslint-disable-next-line prefer-const
		let [hours, minutes] = time.split(":");

		if (hours === "12") {
			hours = "00";
		}

		if (modifier === "PM") {
			hours = parseInt(hours, 10) + 12;
		}

		return `${hours}:${minutes}`;
	},

	/**
	 * Generates apprpriate datetime strings for officer timecards when they are edited by a coordinator. Created for FE-1029
	 * @param {String} startDate The start date of the job (must be in `yyyy-mm-dd` format)
	 * @param {Array<String>} timeValues An array containing the start and end times for the timecard
	 * @returns {Array<String>} Array of ISO-formatted datetime values
	 *
	 * @deprecated This method is only used in `JobDetails.js`, so it can be deleted once the logic has been migrated to that file
	 */
	combineDateAndTimecardTimes(startDate, jobTimeZone, [startTime, endTime]) {
		// Calculate job timezone offset from system timezone
		const jZoneTime = new Date(
			`${this.formatDate(new Date(), undefined, jobTimeZone)}T${this.formatTime(new Date(), undefined, jobTimeZone)}`,
		);
		const sZoneTime = new Date(`${this.formatDate(new Date())}T${this.formatTime(new Date())}`);
		const tzDiffMs = jZoneTime.getTime() - sZoneTime.getTime();
		// Format timecard times for creating date object
		const startSplit = startTime.split(":");
		const endSplit = endTime.split(":");
		if (
			(startSplit[1].toLowerCase().includes("PM") && parseInt(startSplit[0], 10) < 12) ||
			(startSplit[1].toLowerCase().includes("AM") && parseInt(startSplit[0], 10) === 12)
		)
			startSplit[0] = parseInt(startSplit[0], 10) + 12;
		if (
			(endSplit[1].toLowerCase().includes("PM") && parseInt(endSplit[0], 10) < 12) ||
			(endSplit[1].toLowerCase().includes("AM") && parseInt(endSplit[0], 10) === 12)
		)
			endSplit[0] = parseInt(endSplit[0], 10) + 12;
		const ciTime = `${startSplit[0]}:${startSplit[1].replace(/[^0-9]/gi, "")}:00.000`;
		const coTime = `${endSplit[0]}:${endSplit[1].replace(/[^0-9]/gi, "")}:00.000`;
		let clockIn = new Date(`${startDate}T${ciTime}`);
		let clockOut = new Date(`${startDate}T${coTime}`);
		if (clockOut.getTime() < clockIn.getTime())
			clockOut = new Date(clockOut.getTime() + 1000 * 60 * 60 * 24);
		// Apply timezone difference offset to date object
		clockIn = new Date(clockIn.getTime() - tzDiffMs);
		clockOut = new Date(clockOut.getTime() - tzDiffMs);
		return [clockIn.toISOString(), clockOut.toISOString()];
	},

	/**
	 * Rounds any number to a specified number of decimal places. This function solves problems encountered
	 * when using functions such as `Number(x)` or `parseFloat(x)` that can result in an unexpected value (i.e. 0.01000000005 instead of 0.01)
	 * @param {String|Number} num A number to round to the specified number of decimal places
	 * @param {Number} places How many decimal places to round the number to
	 * @returns {Number} The provided number properly rounded to the desired number of decimal places
	 */
	roundToDecimal(num, places = 2) {
		const number = Number(num);
		if (Number.isNaN(number)) return 0;
		return Math.round((number + Number.EPSILON) * 10 ** places) / 10 ** places;
	},

	/**
	 * Debounce a function
	 * @param {Function} func Function to call after debounce wait time expires
	 * @param {Number} wait Delay in milliseconds before calling the function. Defaults to 500.
	 */
	debounce(func, wait = 500) {
		let timeout;

		return function executedFunction(...args) {
			const later = () => {
				clearTimeout(timeout);
				func(...args);
			};

			clearTimeout(timeout);
			timeout = setTimeout(later, wait);
		};
	},

	/**
	 * Function to parse UTC to a given timeZone
	 * @param  {string} utcDate A UTC timestamp
	 * @param  {string} timeZone The timezone to convert the UTC timestamp to
	 * @return {Moment} A `moment` object representing the timestamp in the provided timezone
	 *
	 * @deprecated We are attempting to remove `moment` from the application and should not rely on this method any more.
	 * Instances of this function should be replaced with `utils.formatDate` instead.
	 */
	parseUTCDateToTimeZone(utcDate, timeZone) {
		return moment.utc(utcDate).tz(timeZone);
	},

	/**
	 * Transform an ISO string to a string in the format that the HTML5 `datetime-local` input expects
	 *
	 * @param {string} utcDateString
	 * @returns {string}
	 */
	utcDateStringToDateTimeLocalValue(utcDateString) {
		return moment.utc(utcDateString).local().format("YYYY-MM-DDTHH:mm");
	},

	/**
	 * Transform an HTML5 `datetime-local` input value to an ISO string
	 *
	 * @param {string} isoString
	 * @returns {string}
	 */
	dateTimeLocalValueToIsoString(dateTimeLocalValue) {
		return moment(dateTimeLocalValue).toISOString();
	},

	/**
	 * Return a date N months from now
	 *
	 * @param {number} months
	 * @returns {Date}
	 */
	monthsFromNow(months) {
		return moment().add(months, "M").toDate();
	},

	/**
	 * Round a number using banker's logic
	 * @param {Number} num Number to round
	 * @param {Number} decimalPlaces Number of decimal places to round to
	 * @returns {Number} Rounded number
	 * @see {@link https://stackoverflow.com/questions/3108986/gaussian-bankers-rounding-in-javascript}
	 * @see {@link http://jsfiddle.net/27ckp0d4/2/}
	 * @see {@link https://anderly.com/2009/07/28/to-round-up-or-to-round-down-that-is-the-question/}
	 * */
	bankersRound(num, decimalPlaces = 2) {
		const d = decimalPlaces || 0;
		// eslint-disable-next-line no-restricted-properties
		const m = 10 ** d;
		const n = +(d ? num * m : num).toFixed(8); // Avoid rounding errors
		const i = Math.floor(n);
		const f = n - i;
		const e = 1e-8; // Allow for rounding errors in f
		// eslint-disable-next-line eqeqeq, no-nested-ternary
		const r = f > 0.5 - e && f < 0.5 + e ? (i % 2 == 0 ? i : i + 1) : Math.round(n);
		return d ? r / m : r;
	},

	/**
	 * Check if AD error reason is cancellation
	 * @param {Error} error Error return from msalInstance
	 * */
	isADCancellation(error) {
		return error instanceof Error && error.message.includes(B2C_USER_CANCELLED);
	},

	encodeADState(state) {
		return window.btoa(JSON.stringify(state));
	},

	decodeADState(encodedState) {
		if (!encodedState) return {};
		return JSON.parse(window.atob(encodedState));
	},

	isOrganizationEnabled(organizationId) {
		return SENIORITY_DRAFT_ENABLED_ORGANIZATIONS.includes(organizationId);
	},

	validateNumberInputRequired(number, enableLessThanOne = true) {
		let error = false;
		if (Number.isNaN(Number.parseInt(number, 10))) error = true;
		if (!enableLessThanOne && number < 1) error = true;

		return error;
	},

	convertTime12to24(time12h) {
		if (time12h === "") return "";
		const [time, modifier] = time12h.split(" ");
		if (modifier == null || modifier === "") return time12h;
		let hours = time.split(":")[0];
		const minutes = time.split(":")[1];
		if (hours === "12") {
			hours = "00";
		}
		if (modifier === "PM") {
			hours = parseInt(hours, 10) + 12;
		} else {
			hours = parseInt(hours, 10);
		}
		return `${hours < 10 ? "0" : ""}${hours}:${minutes}`;
	},

	getSelectedPayCode(_agencyPayCodes, payCode) {
		if (!payCode || _agencyPayCodes.length < 1) {
			return { label: "Select a proper Pay Code", title: "" };
		}
		const selectedPayCode = _agencyPayCodes.filter(p => p.value === payCode)[0];
		if (!selectedPayCode) {
			return { label: "Select a proper Pay Code", title: "" };
		}
		return {
			label:
				selectedPayCode.label.length > 20
					? `${selectedPayCode.label.substring(0, 19)}...`
					: selectedPayCode.label,
			title: selectedPayCode.label,
		};
	},

	getSelectedPTAEOCode(_agencyPTAEOCodes, ptaeoCode) {
		if (!ptaeoCode || _agencyPTAEOCodes.length < 1) {
			return { label: "Select a proper PTAEO Code", title: "" };
		}
		const selectedPTAEOCode = _agencyPTAEOCodes.filter(
			p => p.value === ptaeoCode.toString(),
		)[0];

		if (selectedPTAEOCode === null || selectedPTAEOCode === undefined) {
			return { label: "Select a proper PTAEO Code", title: "" };
		}

		return {
			label:
				selectedPTAEOCode.label.length > 20
					? `${selectedPTAEOCode.label.substring(0, 19)}...`
					: selectedPTAEOCode.label,
			title: selectedPTAEOCode.label,
		};
	},

	formatDates(startDate1, endDate1, startTime1 = "00:00", endTime1 = "23:59") {
		// date format must change if user is on iOS
		const formattedStartDate = this.isIOSDevice
			? startDate1.replace(/-/gi, "/")
			: this.fixIELocaleString(startDate1);
		const formattedEndDate = this.isIOSDevice
			? endDate1.replace(/-/gi, "/")
			: this.fixIELocaleString(endDate1);
		const startDateTime = new Date(
			`${formattedStartDate} ${this.fixIELocaleString(startTime1)}`,
		);
		const endDateTime = new Date(`${formattedEndDate} ${this.fixIELocaleString(endTime1)}`);
		// Handle scenario where Date objects may be invalid for some reason
		if (Number.isNaN(startDateTime.getTime()) || Number.isNaN(endDateTime.getTime())) {
			return { StartDateTime: "", EndDateTime: "" };
		}

		// If endDateTime earlier than startDateTime, add a day to EndDateTime
		// to indicate job ends the next day
		if (endDateTime < startDateTime) {
			endDateTime.setDate(endDateTime.getDate() + 1);
		}

		const startDateTimeMs = startDateTime.getTime() - startDateTime.getTimezoneOffset() * 60000;
		const endDateTimeMs = endDateTime.getTime() - endDateTime.getTimezoneOffset() * 60000;
		// Format dates
		return {
			StartDateTime: new Date(startDateTimeMs).toISOString().slice(0, -1),
			EndDateTime: new Date(endDateTimeMs).toISOString().slice(0, -1),
		};
	},

	isInDateRange(_startDate, paycode, _startTime) {
		const startDate = this.formatDates(
			_startDate,
			_startDate,
			_startTime,
			"23:59",
		).StartDateTime;
		const payCodeStartDate = !paycode.PayCodeUseStartDate ? null : paycode.PayCodeUseStartDate;
		const payCodeEndDate = !paycode.PayCodeUseEndDate ? null : paycode.PayCodeUseEndDate;
		if (
			(startDate >= payCodeStartDate && startDate <= payCodeEndDate) ||
			(startDate >= payCodeStartDate && !payCodeEndDate) ||
			(!payCodeStartDate && startDate <= payCodeEndDate) ||
			!payCodeStartDate ||
			!payCodeEndDate
		)
			return true;

		return false;
	},

	filterPayCodesByDateAndStatus(startDate, rawAgencyPayCodes, _startTime) {
		let payCodes = rawAgencyPayCodes;
		payCodes = payCodes
			.filter(payCode => this.isInDateRange(startDate, payCode, _startTime))
			.filter(el => el.IsActive);

		return this.parsePaycodesToReactSelect(payCodes);
	},

	parsePaycodesToReactSelect(rawAgencyPayCodes) {
		const placeEllipsis = (text, maxLenght) =>
			text.length >= maxLenght ? `${text.substring(0, maxLenght - 1)}...` : text;

		return rawAgencyPayCodes.map(el => ({
			value: el.Id,
			label: (
				<span title={`${el.Name}, ${el.Description}`}>
					{placeEllipsis(`${el.Name}, ${el.Description}`, 48)}
				</span>
			),
		}));
	},

	doItHavePayCodeAdded(positions, isPayCodesEnabled = false) {
		if (!positions || positions.length === 0) return false;
		return (
			positions.some(pos => !!pos.PayCodeId) ||
			positions.some(pos => pos.Rate === 0 && isPayCodesEnabled)
		);
	},

	isPayRateValid(payRateValue) {
		return payRateValue >= 0 && payRateValue !== "";
	},

	formatJobInvoicedType(jobInvoicedTypeId) {
		const { lang } = global;
		switch (jobInvoicedTypeId) {
			case JOB_INVOICED_TYPES.OFFICER_INVOICING:
				return lang.t("Officer Invoicing");
			case JOB_INVOICED_TYPES.AGENCY_INVOICING:
				return lang.t("Agency Invoicing");
			case JOB_INVOICED_TYPES.SCHEDULE_ONLY:
				return lang.t("Schedule Only");
			default:
				return "";
		}
	},

	invariante(condition, format) {
		if (!condition) {
			throw new Error(format);
		}
	},

	areCustomDatesValid(startDate, endDate) {
		// By default, we will test both dates against a yyyy-mm-dd format
		let dateRegex = /(\d{4})-(\d{2})-(\d{2})/;
		const altDateRegex = /(\d{2})\/(\d{2})\/(\d{4})/;
		let isValid = true;
		let selectedStart = null;
		let selectedEnd = null;

		if (startDate !== "" && startDate !== null && startDate !== undefined) {
			const formattedStart = dateRegex.test(startDate)
				? startDate.replace(/-/gi, "/")
				: startDate;
			if (Number(formattedStart.split("/")[0]) < 1800) return false;
			selectedStart = new Date(`${formattedStart} 00:00`);
			dateRegex = /(\d{4})\/(\d{2})\/(\d{2})/;
			isValid = dateRegex.test(formattedStart) || altDateRegex.test(formattedStart);
		}

		if (endDate !== "" && endDate !== null && endDate !== undefined) {
			dateRegex = /(\d{4})-(\d{2})-(\d{2})/;
			const formattedEnd = dateRegex.test(endDate) ? endDate.replace(/-/gi, "/") : endDate;
			if (Number(formattedEnd.split("/")[0]) < 1800) return false;
			selectedEnd = new Date(`${formattedEnd} 00:00`);
			dateRegex = /(\d{4})\/(\d{2})\/(\d{2})/;
			isValid = dateRegex.test(formattedEnd) || altDateRegex.test(formattedEnd);
		}

		// For non-completed jobs, check if the entered start date is on or after the beginning of today

		// We should also make sure the end date is after the start date
		if (
			selectedStart !== undefined &&
			selectedStart !== "" &&
			selectedStart !== null &&
			selectedEnd !== undefined &&
			selectedEnd !== "" &&
			selectedEnd !== null
		) {
			if (selectedStart.getTime() > selectedEnd.getTime()) return false;
		}
		// If all previous checks are met on Safari/IE, we should test against a mm/dd/yyyy format
		return isValid;
	},

	datesAreOnSameDay(first, second) {
		return (
			first.getFullYear() === second.getFullYear() &&
			first.getMonth() === second.getMonth() &&
			first.getDate() === second.getDate()
		);
	},

	splitDateTimeComponents(dateString, timeZone, useMilitaryTime) {
		// Parse the date string with Moment
		const date = moment.utc(dateString).tz(timeZone);

		// Format the date
		const formattedDate = date.format("ddd, MMM D, YYYY");

		// Format the time with optional 24-hour format
		const timeFormat = useMilitaryTime ? "HH:mm" : "hh:mm A";
		const formattedTime = date.format(timeFormat);

		return {
			date: formattedDate,
			time: formattedTime,
		};
	},

	splitDateTimeRange(startDateString, endDateString, timeZone, useMilitaryTime) {
		if (!startDateString || !endDateString) {
			return "--";
		}

		const startDate = moment.utc(startDateString).tz(timeZone);
		const endDate = moment.utc(endDateString).tz(timeZone);

		const timeFormat = useMilitaryTime ? "HH:mm" : "hh:mm A";

		const formattedStartTime = startDate.format(timeFormat);
		const formattedEndTime = endDate.format(timeFormat);

		return `${formattedStartTime}-${formattedEndTime}`;
	},

	formatDateTime(DateString, timeZone, useMilitaryTime) {
		if (!DateString) {
			return null;
		}

		const date = moment.utc(DateString).tz(timeZone);
		const timeFormat = useMilitaryTime ? "HH:mm" : "hh:mm A";

		return date.format(timeFormat);
	},

	encodeQueryParameters(query) {
		const encodedQuery = Object.create(query);

		Object.keys(query).forEach(key => {
			encodedQuery[key] = encodeURIComponent(query[key]);
		});

		return encodedQuery;
	},

	compareLineItemsByDateAndOfficerName(a, b) {
		const startTimeA = new Date(a.JobStartTime);
		const startTimeB = new Date(b.JobStartTime);

		if (startTimeA < startTimeB) {
			return -1;
		}

		if (startTimeA > startTimeB) {
			return 1;
		}

		const officerNameA = a.OfficerName === "TBD" || !a.OfficerName ? "" : a.OfficerName;
		const officerNameB = b.OfficerName === "TBD" || !b.OfficerName ? "" : b.OfficerName;
		// If the dates are the same, compare by officer name
		// If both officer names are empty
		if (!officerNameA && !officerNameB) {
			return 0;
		}

		// If officerNameA is empty and officerNameB is not, sort officerNameB first
		if (!officerNameA) {
			return 1;
		}

		// If officerNameB is empty and officerNameA is not, sort officerNameA first
		if (!officerNameB) {
			return -1;
		}

		// Both officer names are non-empty, use localeCompare
		return officerNameA.localeCompare(officerNameB);
	},

	mapNonRKPayInvoiceStatusIdToLabelType(statusId, voided, archived, estimateFinalized) {
		if (voided) {
			return {
				label: "Void",
				type: "danger",
			};
		}

		if (archived) {
			return {
				label: "Archived",
				type: "success",
			};
		}

		if (estimateFinalized) {
			return {
				label: "Finalized",
				type: "warning",
			};
		}

		switch (statusId) {
			case NONRKPAY_INVOICE_STATUSES.CREATED:
				return {
					label: "Created",
					type: "success",
				};
			case NONRKPAY_INVOICE_STATUSES.PENDING_AGENCY_REVIEW:
				return {
					label: "Pending Agency Review",
					type: "warning",
				};
			case NONRKPAY_INVOICE_STATUSES.PENDING_CLIENT_REVIEW:
				return {
					label: "Pending Client Review",
					type: "warning",
				};
			case NONRKPAY_INVOICE_STATUSES.PENDING_CLIENT_PAYMENT:
				return {
					label: "Pending Client Payment",
					type: "warning",
				};
			case NONRKPAY_INVOICE_STATUSES.PAID:
				return {
					label: "Paid",
					type: "success",
				};
			default:
				return {
					label: "Unknown",
					type: "error",
				};
		}
	},

	parseDateCrossBrowser(dateToParse) {
		return new Date(`${dateToParse}${this.isDesktopSafari || this.isIE ? " 00:00" : "T00:00:00"}`,);
	},

	normalizeDate(date) {
		if (!date) return "";

		if (typeof date === "string") return date.split("T")[0];

		return date.toISOString().split("T")[0];
	},

	normalizeDateToMidnight(date) {
		const normalized = this.normalizeDate(date);
		return normalized ? `${normalized}T00:00:00` : "";
	},

	buildUtcStringFromLocalParsedDate(date) {
		// Subtract the local timezone offset (in milliseconds)
		const adjustedTimestamp = date.getTime() - (date.getTimezoneOffset() * 60000);
		const adjustedDate = new Date(adjustedTimestamp);

		// Use UTC getters to extract date components
		const year = adjustedDate.getUTCFullYear();
		const month = String(adjustedDate.getUTCMonth() + 1).padStart(2, "0"); // Months are zero-indexed.
		const day = String(adjustedDate.getUTCDate()).padStart(2, "0");
		const hours = String(adjustedDate.getUTCHours()).padStart(2, "0");
		const minutes = String(adjustedDate.getUTCMinutes()).padStart(2, "0");
		const seconds = String(adjustedDate.getUTCSeconds()).padStart(2, "0");

		// Return the desired string format (without milliseconds or timezone designator)
		return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
	},
	getNonRKPayDisputeAmendmentStatus(dispute) {
		if(dispute === null || dispute === undefined) {
			return {
				totalDisputeReasons: 0,
				totalCompletedDisputeReasons: 0,
				isDisputeCompleted: true,
			};
		}
		const { totalDisputeReasons, totalCompletedDisputeReasons } = Object.keys(dispute).reduce(
			(acc, key) => {
				const reasons = dispute[key];
				acc.totalDisputeReasons += reasons.length;
				acc.totalCompletedDisputeReasons += reasons.filter(
					reason => reason.Status !== NONRKPAY_DISPUTE_REASON_STATUS.CREATED
				).length;
				return acc;
			},
			{ totalDisputeReasons: 0, totalCompletedDisputeReasons: 0 }
		);

		return {
			totalDisputeReasons,
			totalCompletedDisputeReasons,
			isDisputeCompleted: totalDisputeReasons === totalCompletedDisputeReasons,
		};
	},

	parseDateStringToDateNoTime(dateString) {
		// Create a moment object in UTC, then set it to the start of the day (midnight)
		const m = moment.utc(dateString).startOf("day");
		// Return a Date object based on this UTC time
		return m.toDate();
	},

	mapInvoiceTypeIdToJobEventInvoicedType(invoiceTypeId) {
		switch (invoiceTypeId) {
			case V3_INVOICE_TYPE_ID.AGENCY:
				return JOB_INVOICED_TYPES.AGENCY_INVOICING;
			case V3_INVOICE_TYPE_ID.OFFICER:
				return JOB_INVOICED_TYPES.OFFICER_INVOICING;
			default:
				return null;
		}
	},

	mapJobInvoicedTypeIdToInvoiceTypeId(job) {
		if (!job.JobInvoicedTypeId) {
			return job.IsRKPay ? V3_INVOICE_TYPE_ID.OFFICER : V3_INVOICE_TYPE_ID.AGENCY
		}

		switch (job.JobInvoicedTypeId) {
			case JOB_INVOICED_TYPES.AGENCY_INVOICING:
				return V3_INVOICE_TYPE_ID.AGENCY;
			case JOB_INVOICED_TYPES.OFFICER_INVOICING:
				return V3_INVOICE_TYPE_ID.OFFICER;
			default:
				return null;
		}
	}
};
